import clsx from 'clsx';
import { Loader2, X } from 'lucide-react';
import {
  createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { t } from '../../i18n/index.ts';
import { msg } from '../../i18n/labels.ts';

// ---------------------------------------------------------------- Botones
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-pine text-on-pine hover:brightness-110 active:brightness-95 shadow-soft',
  secondary: 'bg-surface text-ink border border-line hover:border-ink-faint',
  ghost: 'text-ink hover:bg-surface-2',
  quiet: 'text-ink-soft hover:text-ink underline-offset-4 hover:underline',
  danger: 'bg-danger text-white hover:brightness-110',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-[filter,background,border-color,color] disabled:opacity-55',
        size === 'sm' && 'h-8 px-3 text-sm',
        size === 'md' && 'h-10 px-4 text-[15px]',
        size === 'lg' && 'h-12 px-6 text-base',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

export function IconButton({ label, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx('inline-flex size-9 items-center justify-center rounded-full text-ink-soft hover:bg-surface-2 hover:text-ink', className)}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------- Formularios
const fieldBase =
  'w-full rounded-xl border border-line bg-surface px-3.5 text-[15px] text-ink placeholder:text-ink-faint transition-colors hover:border-ink-faint focus:border-pine focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-ochre/40 aria-[invalid=true]:border-danger';

interface FieldWrapProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  children: (id: string, describedBy?: string) => ReactNode;
}

export function Field({ label, hint, error, className, children }: FieldWrapProps) {
  const id = useId();
  const descId = hint || error ? `${id}-desc` : undefined;
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-ink-soft">
          {label}
        </label>
      )}
      {children(id, descId)}
      {(error || hint) && (
        <p id={descId} className={clsx('text-[13px]', error ? 'text-danger' : 'text-ink-faint')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; hint?: ReactNode; error?: string | null; wrapClassName?: string };
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, hint, error, wrapClassName, className, ...rest }, ref) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapClassName}>
      {(id, desc) => <input ref={ref} id={id} aria-describedby={desc} aria-invalid={!!error} className={clsx(fieldBase, 'h-11', className)} data-autofocus={rest.autoFocus || undefined} {...rest} />}
    </Field>
  );
});

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; hint?: ReactNode; error?: string | null; wrapClassName?: string };
export function Textarea({ label, hint, error, wrapClassName, className, ...rest }: TextareaProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapClassName}>
      {(id, desc) => <textarea id={id} aria-describedby={desc} aria-invalid={!!error} className={clsx(fieldBase, 'min-h-24 py-2.5', className)} {...rest} />}
    </Field>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label?: ReactNode; hint?: ReactNode; error?: string | null; wrapClassName?: string };
export function Select({ label, hint, error, wrapClassName, className, children, ...rest }: SelectProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapClassName}>
      {(id, desc) => (
        <select id={id} aria-describedby={desc} aria-invalid={!!error} className={clsx(fieldBase, 'h-11 appearance-none bg-[length:12px] bg-[right_14px_center] bg-no-repeat pr-9', className)}
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237b8a80' stroke-width='1.6' fill='none'/%3E%3C/svg%3E\")" }}
          {...rest}>
          {children}
        </select>
      )}
    </Field>
  );
}

/** Selector segmentado accesible (radiogroup). */
export function Segmented<T extends string>({ value, onChange, options, label, size = 'md' }: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode }>;
  label: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full border border-line bg-surface p-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={clsx(
              'relative rounded-full font-medium transition-colors',
              size === 'sm' ? 'px-3 py-1 text-[13px]' : 'px-4 py-1.5 text-sm',
              active ? 'text-on-pine' : 'text-ink-soft hover:text-ink',
            )}
          >
            {active && <motion.span layoutId={`seg-${label}`} className="absolute inset-0 rounded-full bg-pine" transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }} />}
            <span className="relative">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Checkbox({ label, checked, onChange, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; hint?: ReactNode }) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 size-5 accent-[var(--pine)]" />
      <span>
        <span className="text-[15px] text-ink">{label}</span>
        {hint && <span className="block text-[13px] text-ink-faint">{hint}</span>}
      </span>
    </label>
  );
}

// ---------------------------------------------------------------- Diálogo y panel
export function Dialog({ open, onClose, title, children, size = 'md', footer }: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // autoFocus de React corre antes de abrir el diálogo (aún oculto) y se pierde: se repite ya abierto.
      d.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className={clsx(
        // pointer-events-auto: un diálogo abierto desde una capa flotante (pointer-events-none) heredaría ese valor y no se podría tocar ni escribir en él.
        'pointer-events-auto m-auto max-h-[92dvh] w-[calc(100vw-24px)] overflow-hidden rounded-3xl border border-line bg-surface p-0 text-ink shadow-soft backdrop:bg-[rgb(14_25_22/0.45)] backdrop:backdrop-blur-[2px] open:animate-[dlg_.22s_ease-out]',
        size === 'sm' && 'max-w-md',
        size === 'md' && 'max-w-xl',
        size === 'lg' && 'max-w-3xl',
      )}
    >
      {open && (
        <div className="flex max-h-[92dvh] flex-col">
          <header className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
            <h2 id={titleId} className="text-xl">{title}</h2>
            <IconButton label={t('Cerrar')} onClick={onClose} className="-mr-2 -mt-1">
              <X className="size-5" />
            </IconButton>
          </header>
          <div className="overflow-y-auto px-6 pb-6 pt-2">{children}</div>
          {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-line px-6 py-4">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}

export function Sheet({ open, onClose, children, label }: { open: boolean; onClose: () => void; children: ReactNode; label: string }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          role="dialog"
          aria-label={label}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 32, stiffness: 320 }}
          className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[420px] flex-col border-l border-line bg-surface shadow-soft max-md:top-auto max-md:h-[78dvh] max-md:max-w-none max-md:rounded-t-3xl max-md:border-l-0 max-md:border-t"
        >
          <div className="absolute right-3 top-3 z-10">
            <IconButton label={t('Cerrar panel')} onClick={onClose} className="bg-surface/80 backdrop-blur">
              <X className="size-5" />
            </IconButton>
          </div>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------- Estados
export function Spinner({ label }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-ink-faint">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span className="text-sm">{label ?? t('Cargando')}…</span>
    </div>
  );
}

export function Empty({ title, children, action, icon }: { title: string; children?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      {icon && <div className="mb-4 text-ink-faint">{icon}</div>}
      <h3 className="text-lg">{title}</h3>
      {children && <p className="mt-1.5 max-w-sm text-[15px] text-ink-soft">{children}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const text = error instanceof Error ? error.message : msg('Ocurrió un error inesperado');
  return (
    <p role="alert" className="rounded-xl border border-danger/30 bg-danger/8 px-3.5 py-2.5 text-[14px] text-danger">
      {t(text)}
    </p>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'pine' | 'ochre' | 'danger' | 'ok' }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12.5px] font-medium',
        tone === 'neutral' && 'bg-surface-2 text-ink-soft',
        tone === 'pine' && 'bg-pine text-on-pine',
        tone === 'ochre' && 'bg-ochre/18 text-ink',
        tone === 'danger' && 'bg-danger/12 text-danger',
        tone === 'ok' && 'bg-ok/14 text-ok',
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- Avisos (toasts)
interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'error';
  action?: { label: string; onClick: () => void };
}
const ToastCtx = createContext<(t: Omit<Toast, 'id'>) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((x) => [...x.slice(-2), { ...t, id }]);
    setTimeout(() => setToasts((x) => x.filter((y) => y.id !== id)), t.action ? 7000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6">
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className={clsx(
                'pointer-events-auto flex max-w-md items-center gap-3 rounded-2xl px-4 py-3 text-[14.5px] shadow-soft',
                t.tone === 'ok' ? 'bg-pine text-on-pine' : 'bg-danger text-white',
              )}
            >
              <span>{t.text}</span>
              {t.action && (
                <button className="font-semibold underline underline-offset-4" onClick={t.action.onClick}>
                  {t.action.label}
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastCtx.Provider>
  );
}
